import { Box, Button, Card, CardBody, CardHeader, CheckBox, Heading, Image, Markdown, Text, TextInput } from "grommet";
import { Checkmark, Copy, Update, Upload } from "grommet-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGenerateDonatelloApiKeyMutation, useUpdatePaymentOptionMutation } from "../../api/paymentOptionsApi";
import { useDispatch } from "react-redux";
import { addNotification } from "../../slices/notificationsSlice";
import { FadedAccordion } from "../layout/FadedAccordion";
import { InfoSection } from "../layout/InfoSection";
import { DonatelloTipsTranslation } from "../../md-translation/DonatelloTipsTranslation";
import { TextInputWithButton } from "../../forms/TextInputWithButton";

export const DonatelloPaymentMethodCard = ({ enabled, apiKey, nickname, helpLink }) => {

    const { t, ready, i18n } = useTranslation('donatello-settings', {
        useSuspense: false,
    });

    const dispatch = useDispatch();

    const [generateApiKey, { isLoading: isGeneratingApiKey }] = useGenerateDonatelloApiKeyMutation();
    const [updatePaymentOption] = useUpdatePaymentOptionMutation();

    const [callbackUrl, setCallbackUrl] = useState(`${process.env.REACT_APP_API_BASE_URL}/payments/donatello/callback`);

    const [apiCopied, setApiCopied] = useState(false);
    const [callbackCopied, setCallbackCopied] = useState(false);

    const [donatePageName, setDonatePageName] = useState(nickname);

    const handleApiKeyCopy = () => {
        navigator.clipboard.writeText(apiKey)
        setApiCopied(true);
        dispatch(addNotification({ show: true, message: 'Api key successfully copied.', status: 'normal' }))
        setTimeout(() => {
            setApiCopied(false);
        }, 1000)
    }

    const handleDonationCallbackCopy = () => {
        navigator.clipboard.writeText(callbackUrl)
        setCallbackCopied(true);
        dispatch(addNotification({ show: true, message: 'Callback url successfully copied.', status: 'normal' }))
        setTimeout(() => {
            setCallbackCopied(false);
        }, 1000)
    }

    const handleApiKeyGenerate = () => {
        generateApiKey()
        .unwrap()
        .then(response => dispatch(addNotification({ show: true, message: 'New api key successfully generated.', status: 'normal' })))
        .catch(error => dispatch(addNotification({ show: true, message: 'Unable to generate new api key.', status: 'critical' })))
    }

    const handleEnabledToggle = (value) => {
        updatePaymentOption(value)
        .unwrap()
        .then(response => dispatch(addNotification({ show: true, message: `Payment option ${value.enabled ? 'enabled' : 'disabled'}.`, status: 'normal' })))
        .catch(error => dispatch(addNotification({ show: true, message: `Unable to ${value.enabled ? 'enabled' : 'disabled'} payment option.`, status: 'critical' }))) 
    }

    const handleDonatePageName = () => {
        updatePaymentOption({ type: 'DONATELLO', nickname: donatePageName })
        .unwrap()
        .then(response => dispatch(addNotification({ show: true, message: `Donate page name successfully saved.`, status: 'normal' })))
        .catch(error => dispatch(addNotification({ show: true, message: `Unable to save donate page name.`, status: 'critical' })))
    }

    return (
        <Card>
            <CardHeader pad={{ top: 'medium', left: 'medium', right: 'medium' }}>
                <Box direction="row" gap="small" align="center">
                    <Image height={'25px'} width={'150px'} src={`${process.env.PUBLIC_URL}/images/donatello_300x50.png`} /><Heading margin={{ top: 'none', bottom: 'none' }} level={2} size="medium" weight={'bold'}>{t('title')}</Heading>
                </Box>
                <CheckBox toggle pad={'none'} checked={enabled} onClick={(event) => handleEnabledToggle({ type: 'DONATELLO', enabled: event.target.checked })} />
            </CardHeader>
            <CardBody direction="row-responsive" gap="medium" responsive>
                <Box basis="50%" border={{ color: 'background-contrast', side: 'top', size: 'small' }}>
                    <Box margin={{ top: 'medium', bottom: 'small' }}>
                        <Box direction="row" gap="xsmall">
                            <Text>{t('step1')}</Text>
                            <Button href={helpLink} target="_blank" style={{ textDecoration: 'underline' }}>
                                {t('step1-link')}
                            </Button>
                        </Box>
                        <Markdown>
                            {t('step2')}
                        </Markdown>
                        <Markdown>
                            {t('step3')}
                        </Markdown>
                        <Markdown>
                            {t('step4')}
                        </Markdown>
                    </Box>
                    <TextInputWithButton type="password" value={apiKey} width={'medium'}>
                        <Button style={{ borderTopRightRadius: '0', borderBottomRightRadius: '0', borderTopLeftRadius: '0', borderBottomLeftRadius: '0' }} icon={apiCopied ? <Checkmark/> : <Copy/>} onClick={handleApiKeyCopy}/>
                        <Box flex="false" animation={isGeneratingApiKey ? 'rotateRight' : ''}>
                            <Button style={{ borderTopRightRadius: '6px', borderBottomRightRadius: '6px', borderTopLeftRadius: '0', borderBottomLeftRadius: '0' }} icon={<Update/>} onClick={handleApiKeyGenerate}/>
                        </Box>
                    </TextInputWithButton>
                    {/* <Box direction="row" border round="xsmall" width={'medium'}>
                        <TextInput plain type="password" value={apiKey}/>
                        <Button icon={apiCopied ? <Checkmark/> : <Copy/>} onClick={handleApiKeyCopy}/>
                        <Box flex="false" animation={isGeneratingApiKey ? 'rotateRight' : ''}>
                            <Button icon={<Update/>} onClick={handleApiKeyGenerate}/>
                        </Box>
                    </Box> */}
                    <Box margin={{top: 'small', bottom: 'small'}}>
                        <Markdown>
                            {t('step5')}
                        </Markdown>
                    </Box>
                    <TextInputWithButton value={callbackUrl} readOnly width={'medium'}>
                        <Button style={{ borderTopRightRadius: '6px', borderBottomRightRadius: '6px', borderTopLeftRadius: '0', borderBottomLeftRadius: '0' }} icon={callbackCopied ? <Checkmark/> : <Copy/>} onClick={handleDonationCallbackCopy}/>
                    </TextInputWithButton>
                    {/* <Box direction="row" border round="xsmall" width={'medium'}>
                        <TextInput plain type="text" value={callbackUrl} readOnly/>
                        <Button icon={callbackCopied ? <Checkmark/> : <Copy/>} onClick={handleDonationCallbackCopy}/>
                    </Box> */}
                    <Box margin={{top: 'small', bottom: 'small'}}>
                        <Markdown>
                            {t('step6')}
                        </Markdown>
                    </Box>
                    <TextInputWithButton type="text" placeholder="Enter nickname here" value={donatePageName} onChange={(event) => setDonatePageName(event.target.value)} width={'medium'}>
                        <Button style={{ borderTopRightRadius: '6px', borderBottomRightRadius: '6px', borderTopLeftRadius: '0', borderBottomLeftRadius: '0' }} icon={<Upload />} onClick={handleDonatePageName}/>
                    </TextInputWithButton>
                    {/* <Box direction="row" border round="xsmall" width={'medium'}>
                        <TextInput plain type="text" placeholder="Enter nickname here" value={donatePageName} onChange={(event) => setDonatePageName(event.target.value)}/>
                        <Button icon={<Upload />} onClick={handleDonatePageName}/>
                    </Box> */}
                </Box>
                <Box basis="50%">
                    <FadedAccordion previewHeight={300}>
                        <InfoSection>
                            <Box>
                                <Markdown components={{ p: { props: { style: { maxWidth: '100%' } } }, img: { props: { style: { width: '100%' } } } }}>{DonatelloTipsTranslation[i18n.language]}</Markdown>
                            </Box>
                        </InfoSection>
                    </FadedAccordion>
                </Box>
            </CardBody>
        </Card>
    )
}